import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { isNil } from 'lodash';

import { useStyles } from '~/assets/styles';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import RadioWithButtonWrapperFormik from '~/components/core/Formik/RadioWithButtonWrapperFormik';
import type {
  MoiAssigneeMethodStandardParams,
  MoiFormikValues,
  MoiStepProps,
} from '~/components/exposures/moi/MoiStepper/types';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import cn from '~/Utils/cn';

import AssigneeSelectField from './AssigneeSelectField';
import useSelectVendorContact from './useSelectVendorContact';

export interface SelectAssigneeStepProps extends MoiStepProps, MoiAssigneeMethodStandardParams {}

const SelectAssigneeStep: React.FC<SelectAssigneeStepProps> = ({
  setSelectedContact,
  getIsContactVendorApi,
  setIsXactContractor,
  setIsVendorApi,
  exposure,
  default_select,
  disable_select,
  alert_content,
  automatic_rules_assignee,
  setAlertMessage,
  alertMessage,
  moiMethodsReturn,
  ...rest
}) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<MoiFormikValues>();
  const { classes } = useStyles();
  const selectVendorContact = useSelectVendorContact({
    getIsContactVendorApi,
    setIsVendorApi,
    setIsXactContractor,
    setSelectedContact,
    moiMethodsReturn,
  });

  const { isMoiMethodOnlyForServiceProviders, moiMethodsById } = moiMethodsReturn;
  const isAutomaticRulesOffered = !isNil(automatic_rules_assignee);
  // A click on another assignee type or leaving the step cancels an apply whose lookups are in flight
  const automaticRulesApplyIdRef = useRef(0);
  const cancelAutomaticRulesAssignee = () => {
    automaticRulesApplyIdRef.current += 1;
  };

  const applyAutomaticRulesAssignee = () => {
    const applyId = automaticRulesApplyIdRef.current;
    setFieldValue('user_id', undefined);
    setFieldTouched('user_id', false);
    setSelectedContact(undefined);
    setFieldValue('contact_id', undefined);
    setFieldTouched('contact_id', false);
    if (automatic_rules_assignee?.contact) {
      // The route serializes the contact service's ContactInfo; the stepper holds every contact as the swagger
      // ContactFullModel, which types the same optional fields without null
      selectVendorContact(
        automatic_rules_assignee.contact as ContactFullModel,
        () => automaticRulesApplyIdRef.current !== applyId
      );
    }
  };

  const assigneeType = values['assignee_type'];
  useEffect(() => {
    const offeredAssigneeTypes = isAutomaticRulesOffered
      ? ['internal_user', 'service_provider', 'automatic_rules']
      : ['internal_user', 'service_provider'];
    if (!assigneeType || !offeredAssigneeTypes.includes(assigneeType)) {
      setFieldValue('assignee_type', default_select);

      // reset relevant fields if originated from override search vendor flow, or from an Automatic rules choice
      // the response no longer offers
      setFieldValue('user_id', undefined);
      setFieldTouched('user_id', false);
      setFieldValue('selectedVendor', undefined);
      setFieldValue('contact_id', undefined);
      setFieldTouched('contact_id', false);
      setFieldValue('vendor_type', '');
    }
  }, [assigneeType, default_select, isAutomaticRulesOffered, setFieldTouched, setFieldValue]);

  // Every visit to the step fetches the rules' vendor again, while the form keeps the previous visit's choice.
  // When the option is no longer offered this is what drops the previous vendor: the reset effect clears the form
  // fields but leaves the selected contact.
  useEffect(() => {
    if (values['assignee_type'] === 'automatic_rules') {
      applyAutomaticRulesAssignee();
    }
    return cancelAutomaticRulesAssignee;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (alert_content && alertMessage?.message !== alert_content) {
      setAlertMessage({
        message: alert_content,
        type: 'warning',
      });
    }
  }, [alertMessage?.message, alert_content, setAlertMessage]);

  return (
    <div
      className={cn('grid grid-cols-12 gap-32', {
        ['text-slate-900']: values['moi_method_id'] || values['method'],
        ['text-slate-700']: !(values['moi_method_id'] || values['method']),
      })}
    >
      <div className="col-span-3">
        <RadioWithButtonWrapperFormik
          disabled={
            disable_select ||
            !values['moi_method_id'] ||
            isMoiMethodOnlyForServiceProviders(moiMethodsById[values['moi_method_id']])
          }
          id="assignee_type"
          className={classes.radioOrCheckboxWithIconOverride}
          value="internal_user"
          text="Internal User"
          onClick={() => {
            cancelAutomaticRulesAssignee();
            setSelectedContact(undefined);
            setFieldValue('contact_id', undefined);
            setFieldTouched('contact_id', false);
          }}
        />
        <ErrorHelperTextFormik className="mt-0" id="assignee_type" />
      </div>
      <div className="col-span-3">
        <RadioWithButtonWrapperFormik
          disabled={disable_select || !values['moi_method_id']}
          id="assignee_type"
          className={classes.radioOrCheckboxWithIconOverride}
          value="service_provider"
          text="Service Provider"
          onClick={() => {
            cancelAutomaticRulesAssignee();
            setFieldValue('user_id', undefined);
            setFieldTouched('user_id', false);
          }}
        />
        <ErrorHelperTextFormik className="mt-0" id="assignee_type" />
      </div>
      {isAutomaticRulesOffered && (
        <div className="col-span-3">
          <RadioWithButtonWrapperFormik
            disabled={disable_select || !values['moi_method_id']}
            id="assignee_type"
            className={classes.radioOrCheckboxWithIconOverride}
            value="automatic_rules"
            text="Automatic rules"
            onClick={applyAutomaticRulesAssignee}
          />
          <ErrorHelperTextFormik className="mt-0" id="assignee_type" />
        </div>
      )}
      <AssigneeSelectField
        setSelectedContact={setSelectedContact}
        getIsContactVendorApi={getIsContactVendorApi}
        setIsXactContractor={setIsXactContractor}
        setIsVendorApi={setIsVendorApi}
        exposure={exposure}
        setAlertMessage={setAlertMessage}
        alertMessage={alertMessage}
        moiMethodsReturn={moiMethodsReturn}
        automaticRulesAssignee={automatic_rules_assignee}
        {...rest}
      />
    </div>
  );
};

export default SelectAssigneeStep;
